// Autocomplete del comune per l'addizionale comunale IRPEF.
// I dati (generati da scripts/build-addizionali-comunali.js, fonte MEF) sono
// divisi per regione e caricati on-demand alla selezione della regione, cosi'
// il bundle principale non cresce.
//
// Il selettore e' un combobox custom (input + dropdown gestita da noi) invece
// del <datalist> nativo: quest'ultimo ha comportamenti incoerenti tra browser
// e soprattutto su mobile (a volte non compare, filtra diversamente, non si
// puo' stilizzare). Il combobox custom funziona in modo identico ovunque e
// supporta tastiera, mouse e touch.

// Le chiavi corrispondono alle option del select #regione. Import literal per
// permettere a Parcel il code splitting.
const REGIONE_LOADERS = {
  'Abruzzo': () => import('/data/addizionali-comunali/abruzzo.json'),
  'Basilicata': () => import('/data/addizionali-comunali/basilicata.json'),
  'Bolzano': () => import('/data/addizionali-comunali/bolzano.json'),
  'Calabria': () => import('/data/addizionali-comunali/calabria.json'),
  'Campania': () => import('/data/addizionali-comunali/campania.json'),
  'Emilia-Romagna': () => import('/data/addizionali-comunali/emilia-romagna.json'),
  'Friuli Venezia-Giulia': () => import('/data/addizionali-comunali/friuli-venezia-giulia.json'),
  'Lazio': () => import('/data/addizionali-comunali/lazio.json'),
  'Liguria': () => import('/data/addizionali-comunali/liguria.json'),
  'Lombardia': () => import('/data/addizionali-comunali/lombardia.json'),
  'Marche': () => import('/data/addizionali-comunali/marche.json'),
  'Molise': () => import('/data/addizionali-comunali/molise.json'),
  'Piemonte': () => import('/data/addizionali-comunali/piemonte.json'),
  'Puglia': () => import('/data/addizionali-comunali/puglia.json'),
  'Sardegna': () => import('/data/addizionali-comunali/sardegna.json'),
  'Sicilia': () => import('/data/addizionali-comunali/sicilia.json'),
  'Toscana': () => import('/data/addizionali-comunali/toscana.json'),
  'Trento': () => import('/data/addizionali-comunali/trento.json'),
  'Umbria': () => import('/data/addizionali-comunali/umbria.json'),
  "Valle d'Aosta": () => import('/data/addizionali-comunali/valle-d-aosta.json'),
  'Veneto': () => import('/data/addizionali-comunali/veneto.json'),
};

// Massimo numero di risultati mostrati nella dropdown: per regioni con
// migliaia di comuni evita liste ingestibili e DOM inutilmente grande.
const MAX_RISULTATI = 50;

const etichettaComune = ([nome, pr]) => `${nome} (${pr})`;

// Normalizza per il confronto: rimuove accenti e maiuscole cosi' "forli"
// trova "Forli'" e la ricerca e' insensibile ad accenti/maiuscole.
const normalizza = (s) => s
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

// Aliquota mostrata nel campo percentuale: per i comuni a scaglioni si usa
// l'aliquota massima come riferimento visivo; il calcolo vero usa gli scaglioni.
const aliquotaIndicativa = (record) => {
  const valore = record[2];
  if (Array.isArray(valore)) return Math.max(...valore.map(([, a]) => a));
  return valore;
};

let comuniCorrenti = new Map(); // etichetta -> record [nome, pr, aliquota|scaglioni, esenzione]
let recordsCorrenti = [];       // stessi record in array, per il filtro della dropdown
let selezione = null;
let impostazioneProgrammatica = false;

// Regola del comune selezionato, da usare nel calcolo e nei parametri condivisi.
// null quando l'utente non ha scelto un comune (o ha modificato a mano l'aliquota).
export function getComuneSelezionato() {
  if (!selezione) return null;
  const [nome, pr, valore, esenzione] = selezione;
  return {
    nome: `${nome} (${pr})`,
    esenzione,
    scaglioni: Array.isArray(valore) ? valore : null,
    aliquota: Array.isArray(valore) ? null : valore,
  };
}

export function initComuneAutocomplete() {
  const regioneSelect = document.getElementById('regione');
  const comuneInput = document.getElementById('comune');
  const dropdown = document.getElementById('comuni-list');
  const aliquotaInput = document.getElementById('addizionale-comunale');
  const nota = document.getElementById('comune-note');
  if (!regioneSelect || !comuneInput || !dropdown || !aliquotaInput) return;

  const percentuale = (n) => `${n.toFixed(2).replace('.', ',')}%`;
  const euro = (n) => `${n.toLocaleString('it-IT')} €`;

  // -- Stato della dropdown --------------------------------------------------
  let risultati = [];   // record attualmente mostrati
  let attivo = -1;      // indice dell'opzione evidenziata (tastiera)
  let aperta = false;
  let chiudiTimer = null;

  const aggiornaNota = () => {
    if (!nota) return;
    if (!selezione) {
      nota.textContent = "Seleziona il tuo comune: l'addizionale comunale ufficiale viene applicata in automatico.";
      return;
    }
    const [, , valore, esenzione] = selezione;
    const testoAliquota = Array.isArray(valore)
      ? `Addizionale comunale a scaglioni, fino allo ${percentuale(aliquotaIndicativa(selezione))}`
      : `Addizionale comunale: ${percentuale(valore)}`;
    const testoEsenzione = esenzione > 0
      ? `, esente fino a ${euro(esenzione)} di imponibile`
      : '';
    nota.textContent = `${testoAliquota}${testoEsenzione}. Fonte MEF.`;
  };

  const azzeraSelezione = () => {
    selezione = null;
    comuneInput.classList.remove('comune-riconosciuto');
    // il campo aliquota non e' piu' visibile: senza comune torna al default
    impostazioneProgrammatica = true;
    aliquotaInput.value = '0.80';
    aliquotaInput.dispatchEvent(new Event('change'));
    impostazioneProgrammatica = false;
    aggiornaNota();
  };

  const applicaSelezione = (record) => {
    selezione = record;
    impostazioneProgrammatica = true;
    aliquotaInput.value = aliquotaIndicativa(record).toFixed(2);
    aliquotaInput.dispatchEvent(new Event('change'));
    impostazioneProgrammatica = false;
    comuneInput.classList.add('comune-riconosciuto');
    aggiornaNota();
  };

  // -- Dropdown custom -------------------------------------------------------
  const chiudi = () => {
    if (!aperta) return;
    aperta = false;
    dropdown.classList.add('hidden');
    dropdown.replaceChildren();
    comuneInput.setAttribute('aria-expanded', 'false');
    comuneInput.removeAttribute('aria-activedescendant');
    attivo = -1;
    risultati = [];
  };

  const filtra = (query) => {
    const nq = normalizza(query);
    if (!nq) return recordsCorrenti.slice(0, MAX_RISULTATI);
    const iniziano = [];
    const contengono = [];
    for (const record of recordsCorrenti) {
      const nome = normalizza(record[0]);
      const idx = nome.indexOf(nq);
      if (idx === 0) iniziano.push(record);
      else if (idx > 0) contengono.push(record);
    }
    return iniziano.concat(contengono).slice(0, MAX_RISULTATI);
  };

  const evidenzia = (indice) => {
    const opzioni = dropdown.children;
    if (attivo >= 0 && opzioni[attivo]) opzioni[attivo].classList.remove('active');
    attivo = indice;
    const opzione = opzioni[attivo];
    if (opzione) {
      opzione.classList.add('active');
      comuneInput.setAttribute('aria-activedescendant', opzione.id);
      opzione.scrollIntoView({ block: 'nearest' });
    } else {
      comuneInput.removeAttribute('aria-activedescendant');
    }
  };

  const scegli = (record) => {
    comuneInput.value = etichettaComune(record);
    applicaSelezione(record);
    chiudi();
  };

  const mostra = () => {
    if (comuneInput.disabled) return;
    risultati = filtra(comuneInput.value);
    dropdown.replaceChildren();
    attivo = -1;

    if (!risultati.length) {
      const vuoto = document.createElement('li');
      vuoto.className = 'comune-empty';
      vuoto.textContent = 'Nessun comune trovato';
      dropdown.appendChild(vuoto);
    } else {
      const frammento = document.createDocumentFragment();
      risultati.forEach((record, i) => {
        const opzione = document.createElement('li');
        opzione.className = 'comune-option';
        opzione.id = `comune-opt-${i}`;
        opzione.setAttribute('role', 'option');
        opzione.dataset.indice = String(i);

        const spanNome = document.createElement('span');
        spanNome.className = 'comune-nome';
        spanNome.textContent = record[0];
        const spanPr = document.createElement('span');
        spanPr.className = 'comune-pr';
        spanPr.textContent = `(${record[1]})`;
        opzione.append(spanNome, spanPr);
        frammento.appendChild(opzione);
      });
      dropdown.appendChild(frammento);
    }

    dropdown.classList.remove('hidden');
    comuneInput.setAttribute('aria-expanded', 'true');
    aperta = true;
  };

  const caricaRegione = async (regione) => {
    comuniCorrenti = new Map();
    recordsCorrenti = [];
    chiudi();
    const loader = REGIONE_LOADERS[regione];
    if (!loader) {
      comuneInput.disabled = true;
      comuneInput.placeholder = 'Seleziona prima la regione';
      return;
    }
    try {
      const modulo = await loader();
      const dati = modulo?.default ?? modulo;
      recordsCorrenti = dati.comuni;
      for (const record of dati.comuni) {
        comuniCorrenti.set(etichettaComune(record), record);
      }
      comuneInput.disabled = false;
      comuneInput.placeholder = 'Cerca il tuo comune...';
      // ripristino da URL condiviso: se il campo ha gia' un valore, riabbina
      if (comuneInput.value) riconosciComune();
    } catch (err) {
      // dati non disponibili: il campo manuale resta pienamente funzionante
      console.error('addizionali comunali non caricate', err);
      comuneInput.disabled = true;
    }
  };

  // Riconosce un comune quando il testo digitato corrisponde esattamente a
  // un'etichetta (es. ripristino da URL o testo incollato); altrimenti azzera.
  const riconosciComune = () => {
    const record = comuniCorrenti.get(comuneInput.value.trim());
    if (record) applicaSelezione(record);
    else azzeraSelezione();
  };

  // -- Eventi ----------------------------------------------------------------
  regioneSelect.addEventListener('change', () => {
    azzeraSelezione();
    comuneInput.value = '';
    caricaRegione(regioneSelect.value);
  });

  comuneInput.addEventListener('input', () => {
    riconosciComune();
    mostra();
  });

  comuneInput.addEventListener('change', riconosciComune);

  comuneInput.addEventListener('focus', () => {
    if (chiudiTimer) { clearTimeout(chiudiTimer); chiudiTimer = null; }
    mostra();
  });

  // Chiusura al blur con piccolo ritardo, cosi' il tap/click su un'opzione
  // (che avviene dopo il blur su alcuni dispositivi) fa in tempo a registrarsi.
  comuneInput.addEventListener('blur', () => {
    chiudiTimer = setTimeout(chiudi, 150);
  });

  comuneInput.addEventListener('keydown', (e) => {
    if (!aperta && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      mostra();
      e.preventDefault();
      return;
    }
    if (!aperta) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (risultati.length) evidenzia((attivo + 1) % risultati.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (risultati.length) evidenzia((attivo - 1 + risultati.length) % risultati.length);
        break;
      case 'Enter':
        if (attivo >= 0 && risultati[attivo]) {
          e.preventDefault();
          scegli(risultati[attivo]);
        }
        break;
      case 'Escape':
        chiudi();
        break;
      default:
        break;
    }
  });

  // mousedown sul contenitore: preventDefault mantiene il focus sull'input
  // (evita che il blur chiuda la lista prima del click). La selezione avviene
  // sul click, che scatta anche dopo un tap su touch ma non dopo uno scroll.
  dropdown.addEventListener('mousedown', (e) => e.preventDefault());
  dropdown.addEventListener('click', (e) => {
    const opzione = e.target.closest('.comune-option');
    if (!opzione) return;
    const record = risultati[Number(opzione.dataset.indice)];
    if (record) scegli(record);
  });

  // modifica manuale dell'aliquota = override: il calcolo torna alla
  // percentuale flat inserita dall'utente
  aliquotaInput.addEventListener('input', () => {
    if (!impostazioneProgrammatica) azzeraSelezione();
  });

  // regione gia' valorizzata al load (es. ritorno dalla pagina risultati)
  if (regioneSelect.value) caricaRegione(regioneSelect.value);
  aggiornaNota();
}
